import React, { useState } from 'react'
import { Dropdown } from 'react-bootstrap'

import useTranslation from '@/Hooks/UseTranslation'

export interface ResponsiveToolbarAction {
  id?: string
  label: string
  icon: JSX.Element
  onClick?: () => void
  className?: string
  disabled?: boolean
  active?: boolean
  buttonRef?: React.Ref<HTMLButtonElement>
  desktopContent?: React.ReactNode
  mobileContent?: React.ReactNode
}

interface Props {
  actions?: ResponsiveToolbarAction[]
  actionsClassName?: string
  className?: string
  primaryAction?: React.ReactNode
  primaryPosition?: 'start' | 'end'
  temporalControl?: React.ReactNode
}

const ResponsivePageToolbar = ({
  actions = [],
  actionsClassName,
  className,
  primaryAction,
  primaryPosition = 'start',
  temporalControl,
}: Props) => {

  const { t } = useTranslation()
  const [showMore, setShowMore] = useState(false)
  const [openSubmenu, setOpenSubmenu] = useState('')

  const closeMore = () => {
    setShowMore(false)
    setOpenSubmenu('')
  }

  const runAction = (action: ResponsiveToolbarAction) => {
    if (action.disabled || !action.onClick) return

    action.onClick()
    closeMore()
  }

  return (
    <>
      <div className={`ul-widget__head-toolbar pl4-responsive-page-toolbar ${className || ''}`}>
        <div className={`text-right pl4-responsive-page-toolbar__actions ${actionsClassName || ''}`}>
          {primaryAction && primaryPosition === 'start' && (
            <div className="pl4-responsive-page-toolbar__primary">
              {primaryAction}
            </div>
          )}

          <div className="pl4-responsive-page-toolbar__desktop-actions">
            {temporalControl}
            {actions.map((action, index) => {
              const actionId = action.id || String(index)

              return action.desktopContent ? (
                <React.Fragment key={actionId}>{action.desktopContent}</React.Fragment>
              ) : (
              <button
                key={actionId}
                ref={action.buttonRef}
                type="button"
                className={`btn btn-link btn-link-header pl4-page-action--secondary ${action.className || ''} ${action.active ? 'active' : ''}`}
                disabled={action.disabled}
                aria-label={action.label}
                title={action.label}
                onClick={action.onClick}
              >
                {action.icon}
                <span>{action.label}</span>
              </button>
              )
            })}
          </div>

          {primaryAction && primaryPosition === 'end' && (
            <div className="pl4-responsive-page-toolbar__primary">
              {primaryAction}
            </div>
          )}

          {actions.length > 0 && (
            <Dropdown
              alignRight
              className="pl4-mobile-more-actions"
              show={showMore}
              onToggle={(nextShow) => {
                setShowMore(nextShow)
                if (!nextShow) setOpenSubmenu('')
              }}
            >
              <Dropdown.Toggle
                as="button"
                type="button"
                className="btn btn-link btn-link-header pl4-page-action--secondary pl4-mobile-more-actions__toggle"
                aria-label={t('SYSTEM:MoreActions')}
                title={t('SYSTEM:MoreActions')}
              >
                <i className="fas fa-ellipsis-h" aria-hidden="true" />
              </Dropdown.Toggle>

              <Dropdown.Menu className="pl4-mobile-more-actions__menu">
                {actions.map((action, index) => {
                  const actionId = action.id || String(index)

                  if (action.mobileContent) {
                    const expanded = openSubmenu === actionId

                    return (
                      <div className="pl4-mobile-more-actions__group" key={actionId}>
                        <button
                          type="button"
                          className={`pl4-mobile-more-actions__item ${action.className || ''} ${action.active ? 'active' : ''}`}
                          disabled={action.disabled}
                          aria-expanded={expanded}
                          onClick={() => setOpenSubmenu(expanded ? '' : actionId)}
                        >
                          {action.icon}
                          <span>{action.label}</span>
                          <i className={`fas fa-chevron-${expanded ? 'up' : 'down'} pl4-mobile-more-actions__chevron`} aria-hidden="true" />
                        </button>
                        {expanded && (
                          <div className="pl4-mobile-more-actions__submenu">
                            {action.mobileContent}
                          </div>
                        )}
                      </div>
                    )
                  }

                  return (
                    <Dropdown.Item
                      as="button"
                      key={actionId}
                      className={`pl4-mobile-more-actions__item ${action.className || ''} ${action.active ? 'active' : ''}`}
                      disabled={action.disabled}
                      onClick={() => runAction(action)}
                    >
                      {action.icon}
                      <span>{action.label}</span>
                    </Dropdown.Item>
                  )
                })}
              </Dropdown.Menu>
            </Dropdown>
          )}
        </div>
      </div>

      {temporalControl && (
        <div className="pl4-responsive-page-toolbar__mobile-temporal">
          {temporalControl}
        </div>
      )}
    </>
  )
}

export default ResponsivePageToolbar
